<template>
  <v-card class="white-background mt-4">
    <v-card-text class="each-row-height top-bottom-blue font-size--18 font-weight-bold">
      <v-row>
        <v-col color="#233b52" cols="11">
          <span role="heading" aria-level="2">
            <v-icon color="#233b52" :icon="mdiBell" />
            <sup class="redBadgeCircleLarge">
              <span class="warningTextInCircleLarge">!</span>
            </sup>
            {{ $t('L_my_notifications') }}
          </span>
        </v-col>
        <v-col color="#063562" cols="1" class="text-right">
          <div role="heading" aria-level="2">
            <v-icon
              size="24"
              @click="isShowExpandedFlag = !isShowExpandedFlag"
              :icon="showExpandedFlag ? mdiChevronUp : mdiChevronDown"
            />
          </div>
        </v-col>
      </v-row>
    </v-card-text>
    <div
      v-for="(item, index) in getFirstThreeAlertsAndNotifications"
      :key="index + 'alertAndNotify'"
      class="font-size--14"
      :class="index % 2 === 0 ? 'light-blue-background' : 'white-background'"
    >
      <v-card-text class="each-row-height top-bottom-blue font-size--14" v-if="showExpandedFlag">
        <v-row p0 m0>
          <v-col cols="12" class="text-right my-0 py-0">
            <v-icon
              v-show="item.allowDismiss"
              color="#333333"
              class="pl-2 pushXUp font-size--20"
              @click="dismissThenGetAlerts(item.alertId)"
              :icon="mdiClose"
              size="24"
            />
            <span v-show="!item.allowDismiss" color="#e7edf3" class="pl-2 spacing-right-missing-x">
              &nbsp;
            </span>
          </v-col>
          <v-col cols="12" class="text-left my-0 py-0 pb-1">
            <v-icon
              v-if="item.alertIcon && item.alertIcon.toLowerCase() === 'warning'"
              class="mr-4"
              color="orange"
              :icon="mdiAlert"
            />
            <v-icon
              v-else-if="item.alertIcon && item.alertIcon.toLowerCase() === 'info'"
              class="mr-4"
              color="blue"
              :icon="mdiInformation"
            />
            <v-icon
              v-else-if="item.alertIcon && item.alertIcon.toLowerCase() === 'error'"
              class="mr-4"
              color="red"
              :icon="mdiAlertCircle"
            />
            <span color="#333333" class="ml-0 pr-2">
              {{
                $t(item.messages.filter((message: any) => message.language === locale)[0].message)
              }}
            </span>
            <OnexButton
              @click="viewNotification(item)"
              variant="text"
              color="anchor"
              class="text-h5 pl-0"
              >{{
                item.messages.filter((message: any) => message.language === locale)[0].actionText
              }}
            </OnexButton>
          </v-col>
        </v-row>
      </v-card-text>
    </div>
    <v-card-text
      class="each-row-height top-bottom-blue font-size--14"
      v-if="
        showExpandedFlag &&
        alertsAndNotifications &&
        alertsAndNotifications.length > maxNotification
      "
    >
      <v-row>
        <v-col
          class="colorBlue font-size--14 font-weight-bold clickable"
          @click="$router.push({ name: RouteNames.ALERT_AND_NOTIFICATIONS })"
        >
          {{ $t('L_view_all') }}
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script lang="ts">
import { mapActions, mapState } from 'pinia';
import { useGlobalStore } from '@/stores/global/global.store';
import { CapacitorFacade } from '@/plugins/capacitorPlugin';
import { defineComponent, type ComponentOptions } from 'vue';
import {
  mdiChevronDown,
  mdiChevronUp,
  mdiBell,
  mdiAlert,
  mdiInformation,
  mdiAlertCircle,
  mdiClose,
} from '@mdi/js';
import { RouteNames } from '@/router/routeNames';
import { MAX_NOTIFICATIONS_TO_SHOW } from '@/shared/constants.ts';
import { OnexButton } from '@primerica/onex-ui-lib';

const componentOptions: ComponentOptions = {
  name: 'NotificationsComponent',
  props: {
    context: {
      type: String,
      required: false,
      default: '',
    },
  },
  components: {
    OnexButton,
  },
  data: () => ({
    showMoreDiv: false,
    showAll: false,
    isShowExpandedFlag: false,
    mdiChevronUp,
    mdiChevronDown,
    mdiBell,
    mdiAlert,
    mdiInformation,
    mdiAlertCircle,
    mdiClose,
    RouteNames,
    maxNotification: window.appConfig.maxNotificationsToShow,
  }),
  computed: {
    ...mapState(useGlobalStore, {
      alertsAndNotifications: 'alertsAndNotifications',
      notifications: 'notifications',
      locale: 'getLocale',
    }),
    getFirstThreeAlertsAndNotifications() {
      return this.alertsAndNotifications.slice(0, MAX_NOTIFICATIONS_TO_SHOW);
    },
    showExpandedFlag() {
      return this.isShowExpandedFlag;
    },
  },
  methods: {
    ...mapActions(useGlobalStore, [
      'getAlerts',
      'dismissAlert',
      'completeAlert',
      'setShowGoPaperlessWrapper',
      'setGoPaperlessType',
    ]),

    async dismissThenGetAlerts(alertId: string) {
      await this.dismissAlert(alertId);
      this.getAlerts();
    },
    viewNotification(itemObj: { actionType: string; alertId: string }) {
      if (itemObj.actionType === 'PHONE_VERIFICATION') {
        this.$emit('call-verify-phone-wrapper');
      } else if (itemObj.actionType === 'PAPERLESS_COMM') {
        this.setShowGoPaperlessWrapper(true);
        this.setGoPaperlessType('PAPERLESS_COMM');
      } else if (itemObj.actionType === 'PAPERLESS_COMM_INVESTMENT') {
        this.setShowGoPaperlessWrapper(true);
        this.setGoPaperlessType('PAPERLESS_COMM_INVESTMENT');
      } else if (itemObj.actionType === 'ACKNOWLEDGEMENT') {
        this.$emit('view-notification', itemObj.alertId);
      }
    },
  },
  created() {
    if (this.$vuetify.display.smAndDown || CapacitorFacade.isNativePlatform()) {
      this.isShowExpandedFlag = false;
    } else {
      this.isShowExpandedFlag = true;
    }
  },
  mounted() {
    this.getAlerts();
    if (this.notifications?.length > MAX_NOTIFICATIONS_TO_SHOW) {
      this.showMoreDiv = true;
    }
  },
};

export default defineComponent(componentOptions);
</script>

<style lang="scss">
.clickable {
  cursor: pointer;
}
.icon_box {
  max-width: 10px;
}
.redBadgeCircleLarge {
  width: 15px;
  background: #f00;
  position: relative;
  top: -26px;
  height: 15px;
  border-radius: 15px;
  left: -11px;
  display: inline-block;
}
.warningTextInCircleLarge {
  display: inline-block;
  position: relative;
  left: 6px;
  top: 7px;
  font-size: 10px;
  color: #fff;
  font-weight: 800;
}
.borderLeftWall {
  border-left: 1px solid #ccc;
}
.light-blue-background {
  background-color: #f5f7fa;
}
.white-background {
  background-color: #ffffff !important;
}
.blue-background {
  background-color: #e7edf3 !important;
}
.top-bottom-blue {
  border-bottom: 1px solid #b4c9db;
}
.colorBlue {
  color: #0c6dca;
}
.pushXUp {
  margin-top: -2px;
}
.ViewAndVerifyTop {
  padding-top: 4px;
}
.spacing-right-missing-x {
  padding-right: 23px;
}
#notifications {
  position: relative;
  margin-top: 0px;
  padding-left: 0px;
  background-color: black;
  color: white;
  .v-notification.error {
    position: inherit;
  }
  .v-expansion-panel {
    box-shadow: none;
  }
  .theme--light.v-icon {
    color: white;
  }
  .header {
    padding-left: 10px;
  }
  .details {
    padding-left: 34px;
  }
  .title {
    font-size: 40px;
  }
  .circledX {
    float: right;
    color: white;
  }
  @media only screen and (min-width: 1264px) {
    margin-top: 18px;
  }
}
</style>
